import { uuid } from "uuidv4";
import { BaseService } from "../services/_base-service";
import { Api, ApiResponse, StatusCode } from "../utils/api";
import { ApiEndpoints } from "../utils/app-constants";
import { AppUtils } from "../utils/app-utils";
import { toSimpleDateTime } from "../utils/date-utils";
import { fileToBase64 } from "../utils/file-utils";
import { MetricsSearchFilters } from "./models/metrics-search-filters";
import { CreateProductionRunDelivery } from "./models/metrics/create-production-run-delivery";
import { CreateProductionRunOrderIds } from "./models/metrics/create-production-run-order-ids";
import { CreateProductionRunPickup } from "./models/metrics/create-production-run-pickup";
import { UnplannedDeliveryMetricsGroup } from "./models/metrics/unplanned-delivery-metrics-group";
import { UnplannedDeliveryMetricsGroupDto } from "./models/metrics/unplanned-delivery-metrics-group-dto";
import { UnplannedDeliveryOrder } from "./models/metrics/unplanned-delivery-order";
import { UnplannedPickupMetricsGroup } from "./models/metrics/unplanned-pickup-metrics-group";
import { UnplannedPickupMetricsGroupDto } from "./models/metrics/unplanned-pickup-metrics-group-dto";
import { UnplannedPickupOrder } from "./models/metrics/unplanned-pickup-order";
import { UnplannedMetric } from "./models/metrics/unplanned-task-metric";
import { ProductionJobUpdate } from "./models/schedule/production-job-update";
import { ProductionJobUpdateResult } from "./models/schedule/production-job-update-result";
import { ProductionRun } from "./models/schedule/production-run";
import { ProductionRunDto } from "./models/schedule/production-run-dto";
import { RankingsResponse } from "./models/schedule/ranking-query-respons";
import { ScheduleMetric } from "./models/schedule/schedule-metric";
import { UpdateRunRanking } from "./models/schedule/update-run-ranking";
import { CellBase, CellMetric, CellProperty } from "./models/shared/metric-base";
import { Division } from "../models/ordering/enums/division";
import { ScheduleGroup } from "./models/enums/schedule-group";
import * as Plannings from "../transportation/models/planning/_index";
import { ShipmentMethod, ShipmentMethodMetadata } from "../transportation/models/enums/shipment-method";
import { AppliedFilterBarOpt } from "../models/utilities/filter-bar";
import { toShortDate } from "../utils/date-utils";

export class ProductionSvc extends BaseService {

    /**
     * Get all the unplanned pickup production tasks
     * @returns An API response of UnplannedPickupTaskGroup objects 
     */
    public getUnplannedPickupTasks = async (filter: MetricsSearchFilters, isUrgent : boolean) => {

        this._showLoading();
        const response = new ApiResponse<UnplannedPickupMetricsGroup[]>();

        try {

            let url = AppUtils.convertFiltersToUrl(filter.baseUrl, filter);
            
            if (isUrgent === true) {
                url += "&isUrgent=true";
            }

            const prodRuns = await Api.GetList(
                UnplannedPickupMetricsGroupDto,
                url
            );

            const flattened: UnplannedPickupMetricsGroup[] = [];

            if (prodRuns) {

                prodRuns.forEach((itm: UnplannedPickupMetricsGroupDto) => {

                    const tmp = new UnplannedPickupMetricsGroup();
                    tmp.dealerId = itm.dealerId;
                    tmp.dealerName = itm.dealerName;
                    tmp.name = itm.name;
                    tmp.shipmentMethod = itm.shipmentMethod;
                    tmp.shipToAddressId = itm.shipToAddressId;
                    tmp.groupId = itm.groupId;
                    tmp.tmpClientId = uuid();

                    itm.metrics.forEach((dtoItem: UnplannedMetric) => {
                        for (let i = 0; i < tmp.METRIC_LIST.length; i++) {
                            if ((tmp.METRIC_LIST[i] as CellMetric).jobType === dtoItem.jobType) {
                                (tmp.METRIC_LIST[i] as CellMetric).quantity = dtoItem.quantity;
                                (tmp.METRIC_LIST[i] as CellMetric).value = dtoItem.quantity;
                                (tmp.METRIC_LIST[i] as CellMetric).parentRunId = tmp.groupKey;
                                break;
                            }
                        }
                    });

                    const propMetrics = [];
    
                    const metricDealerId = new CellProperty();
                    metricDealerId.value = itm.dealerId;
                    propMetrics.push(metricDealerId);
    
                    const metricDealerName = new CellProperty();
                    metricDealerName.value = itm.name;
                    propMetrics.push(metricDealerName);

                    const metricMethod = new CellProperty();
                    metricMethod.value = ShipmentMethodMetadata[tmp.shipmentMethod]?.display ?? '';
                    propMetrics.push(metricMethod);
    
                    tmp.METRIC_LIST.unshift(...propMetrics);
    
                    tmp.METRIC_LIST.forEach((sitm : CellBase) => {
                        (sitm as CellMetric).parentRunId = tmp.groupKey;
                    });

                    // Add it
                    flattened.push(tmp);

                });

                response.data = flattened;
                response.status = StatusCode.Success;
            }
        }
        catch (err) {
            response.status = this._handleError(err);
        }

        this._hideLoading();
        return response;
    }

    /**
     * Get the orders of a group of the unplanned orders: a dealer's pickups, or the deliveries to a ship to address
     * @param filter The search the group was found by
     * @param group The group to get the orders of
     * @returns An API response of the orders in the group
     */
     public getUnplannedPickupOrders = async (filter : MetricsSearchFilters|undefined, group : UnplannedPickupMetricsGroup, isUrgent : boolean, onlyThisGroup : boolean = true) => {

        this._showLoading();
        const response = new ApiResponse<UnplannedPickupOrder[]>();

        try {

            // Only the orders of the shipment method of the group, whatever methods the search is for, and the
            // dealer of its pickups or the address of its deliveries
            const isDelivery = group.shipmentMethod === ShipmentMethod.Delivery;
            const ofTheDealerOrAddress = isDelivery
                ? `shipToAddressId=${ group.shipToAddressId }`
                : `dealerId=${ group.dealerId }`;

            // the orders of the group alone, or of every group of the dealer or address to split them into groups
            const ofTheGroup = onlyThisGroup
                ? `${ ofTheDealerOrAddress }&groupId=${ group.groupId }`
                : ofTheDealerOrAddress;

            let url = '';
            if (filter !== undefined) {
                const kept = { ...filter, appliedFilters: filter.appliedFilters.filter((f : AppliedFilterBarOpt) => f.qsName !== 'shipmentMethod') };
                url = AppUtils.convertFiltersToUrl(ApiEndpoints.PRODUCTION_METRICS_ORDERS_DETAILS, kept);
                url += `&shipmentMethod=${ group.shipmentMethod }&${ ofTheGroup }`;
            }
            else {
                url = ApiEndpoints.PRODUCTION_METRICS_ORDERS_DETAILS + `?shipmentMethod=${ group.shipmentMethod }&${ ofTheGroup }`;
            }

            if (isUrgent === true) {
                url += "&isUrgent=true";
            }

            const orders = await Api.GetList(
                UnplannedPickupOrder,
                url
            );

            if (orders) {
                response.data = orders;
                response.status = StatusCode.Success;
            }
        }
        catch (err) {
            response.status = this._handleError(err);
        }

        this._hideLoading();
        return response;
    }

    /**
     * Save the groups the orders of a dealer's pickups, or of the deliveries to an address, were split into
     * @param orders The orders that were moved to another group
     */
    public updateOrderGroups = async (orders : UnplannedPickupOrder[]) : Promise<ApiResponse<boolean>> => {

        let response = new ApiResponse<boolean>();

        try {

            const body = new Plannings.OrderGroupRequest();
            orders.forEach((o : UnplannedPickupOrder) => {
                const update = new Plannings.OrderGroupUpdate();
                update.id = o.orderId;
                update.groupId = o.groupId;
                update.rowVersion = o.rowVersion;

                body.updates.push(update);
            });

            const result = await this._post<boolean>(ApiEndpoints.PRODUCTION_METRICS_ORDERS_GROUPS, body);

            response = new ApiResponse<boolean>();
            response.status = result.status;
            response.data = result.data;
        }
        catch (err) {
            response.status = this._handleError(err);
        }

        return response;
    }

    /**
     * Create a new production run for the pickup
     * @param toCreate The create model
     */
     public createProductionRunForPickup = async (toCreate: CreateProductionRunPickup): Promise<ApiResponse<boolean>> => {

        let response = new ApiResponse<boolean>();

        try {

            const result = await this._post<boolean>(
                ApiEndpoints.PRODUCTION_RUN_CREATE_FOR_ORDERS,
                toCreate
            );

            response = new ApiResponse<boolean>();
            response.status = result.status;
            response.data = result.data;
        }
        catch (err) {
            response.status = this._handleError(err);
        }

        return response;
    }

    /**
     * Create a new production run for the orders in a spreadsheet: their IDs are in its first column, with no header row.
     * It is all or nothing: when any of the orders cannot be put into a run, the API says why of each and the error is
     * toasted, and no run is created.
     * @param name The name of the run
     * @param file The Excel spreadsheet of the order IDs
     * @returns An API response that is a success when the run was created
     */
    public createProductionRunForOrderIds = async (name: string, file: File): Promise<ApiResponse<boolean>> => {

        const response = new ApiResponse<boolean>();
        this._showLoading();

        try {
            const req = new CreateProductionRunOrderIds();
            req.name = name;
            req.orderIdsFile = await fileToBase64(file);

            const result = await Api.Post(ApiEndpoints.PRODUCTION_RUN_CREATE_FOR_ORDER_IDS, req);

            response.status = StatusCode.Success;
            response.data = true;

            const count: number = result?.orderCount ?? 0;
            this._showToast(`Production run created from ${ count } ${ count === 1 ? 'order' : 'orders' }.`, 'success');
        }
        catch (err) {
            response.status = this._handleError(err as Error, true);

            // the reasons that orders cannot be put into a run are long, so this toast stays long enough to read them
            const reasons = AppUtils.FormatError(err);
            this._showToast(reasons, 'error', Math.min(20000, Math.max(6000, reasons.length * 60)));
        }

        this._hideLoading();
        return response;
    }

    /**
     * Get all the unplanned delivery production tasks
     * @returns An API response of UnplannedPickupTaskGroup objects 
     */
    public getUnplannedDeliveryTasks = async () => {

        this._showLoading();
        const response = new ApiResponse<UnplannedDeliveryMetricsGroup[]>();

        try {

            const prodRuns = await Api.GetList(
                UnplannedDeliveryMetricsGroupDto,
                ApiEndpoints.PRODUCTION_METRICS_SHIPMENTS
            );

            const flattened: UnplannedDeliveryMetricsGroup[] = [];

            if (prodRuns) {

                prodRuns.forEach((itm: UnplannedDeliveryMetricsGroupDto) => {

                    const tmp = new UnplannedDeliveryMetricsGroup();
                    tmp.shipmentName = itm.shipmentName;
                    tmp.shipmentId = itm.shipmentId;
                    tmp.shipmentStatus = itm.shipmentStatus;
                    tmp.departureTimeLocal = itm.departureTimeLocal;
                    tmp.tmpClientId = uuid();

                    itm.metrics.forEach((dtoItem: UnplannedMetric) => {
                        for (let i = 0; i < tmp.METRIC_LIST.length; i++) {
                            if ((tmp.METRIC_LIST[i] as CellMetric).jobType === dtoItem.jobType) {
                                (tmp.METRIC_LIST[i] as CellMetric).quantity = dtoItem.quantity;
                                (tmp.METRIC_LIST[i] as CellMetric).value = dtoItem.quantity;
                                (tmp.METRIC_LIST[i] as CellMetric).parentRunId = itm.shipmentId;
                                break;
                            }
                        }
                    });

                    const propMetrics = [];

                    const metricShipmentId = new CellProperty();
                    metricShipmentId.value = itm.shipmentId;
                    propMetrics.push(metricShipmentId);

                    const metricShipmentName = new CellProperty();
                    metricShipmentName.value = itm.shipmentName;
                    propMetrics.push(metricShipmentName);

                    const metricDeptTime = new CellProperty();
                    metricDeptTime.value = itm.departureTimeLocal ? toSimpleDateTime(itm.departureTimeLocal) : 'NONE' ;
                    propMetrics.push(metricDeptTime);

                    tmp.METRIC_LIST.unshift(...propMetrics);

                    tmp.METRIC_LIST.forEach((sitm : CellBase) => {
                        (sitm as CellMetric).parentRunId = itm.shipmentId;
                    });

                    // Add it
                    flattened.push(tmp);

                });

                response.data = flattened;
                response.status = StatusCode.Success;
            }
        }
        catch (err) {
            response.status = this._handleError(err);
        }

        this._hideLoading();
        return response;
    }

    /**
     * Get all the unplanned delivery orders for a shipment
     * @returns An API response of UnplannedPickupTaskGroup objects 
     */
    public getUnplannedDeliveryOrders = async (shipmentId : number) => {

        this._showLoading();
        const response = new ApiResponse<UnplannedDeliveryOrder[]>();

        try {

            const orders = await Api.GetList(
                UnplannedDeliveryOrder,
                ApiEndpoints.PRODUCTION_METRICS_SHIPMENTS_ORDERS.formattify(shipmentId)
            );

            if (orders) {
                response.data = orders;
                response.status = StatusCode.Success;
            }
        }
        catch (err) {
            response.status = this._handleError(err);
        }

        this._hideLoading();
        return response;
    }

    /**
     * Create a new production run for the pickup
     * @param toCreate The create model
     */
     public createProductionRunForDelivery = async (toCreate: CreateProductionRunDelivery): Promise<ApiResponse<boolean>> => {

        let response = new ApiResponse<boolean>();

        try {

            const result = await this._post<boolean>(
                ApiEndpoints.PRODUCTION_RUN_CREATE_FOR_SHIPMENT,
                toCreate
            );

            response = new ApiResponse<boolean>();
            response.status = result.status;
            response.data = result.data;
        }
        catch (err) {
            response.status = this._handleError(err);
        }

        return response;
    }

    /**
     * Get the production schedule
     * @param group Which runs to get: those with Complete Door or OEM orders, which have metrics, those with
     * Rolling Steel orders, which have only the loading job, or every run when it is left out
     * @returns A list of production runs
     */
    public getProductionSchedule = async (group?: ScheduleGroup) => {

        this._showLoading();
        const response = new ApiResponse<ProductionRun[]>();

        try {

            const prodRuns = await Api.GetList(
                ProductionRunDto,
                group === 'rs'
                    ? ApiEndpoints.PRODUCTION_SCHEDULE_RS
                    : group === 'cdoem'
                        ? ApiEndpoints.PRODUCTION_SCHEDULE_CDOEM
                        : ApiEndpoints.PRODUCTION_SCHEDULE_ALL
            );

            const flattened: ProductionRun[] = [];

            if (prodRuns) {

                prodRuns.forEach((itm: ProductionRunDto) => {

                    const tmp = new ProductionRun();

                    // The runs of the Rolling Steel schedule have only the loading (MISC) job, which prints their
                    // production paperwork, so a row is its properties and that one metric
                    if (group === 'rs') {
                        tmp.METRIC_LIST = [tmp.METRIC_LOAD_SEC];
                    }

                    // Parent Run Properties
                    tmp.id = itm.id;
                    tmp.name = itm.name;
                    tmp.tmpClientId = uuid();
                    tmp.shipmentId = itm.shipmentId;
                    tmp.departureTimeLocal = itm.departureTimeLocal;
                    tmp.rank = itm.rank;

                    // The server only sends us actual jobs but we need something to
                    // put in all the cells.  So locally we have an empty metric 
                    // collection so we want to loop through the results from the 
                    // server and wherever we find a match based on the jobType, 
                    // we update that information.
                    itm.jobs.forEach((dtoItem: ScheduleMetric) => {
                        for (let i = 0; i < tmp.METRIC_LIST.length; i++) {
                            if ((tmp.METRIC_LIST[i] as CellMetric).jobType === dtoItem.type) {
                                (tmp.METRIC_LIST[i] as CellMetric).quantity = dtoItem.quantity;
                                (tmp.METRIC_LIST[i] as CellMetric).status = dtoItem.status;
                                (tmp.METRIC_LIST[i] as CellMetric).value = dtoItem.id;
                                (tmp.METRIC_LIST[i] as CellMetric).parentRunId = itm.id;
                                break;
                            }
                        }
                    });

                    const propMetrics = [];

                    const metricId = new CellProperty();
                    metricId.value = itm.id;
                    propMetrics.push(metricId);

                    const metricName = new CellProperty();
                    metricName.value = itm.name;
                    propMetrics.push(metricName);

                    const metricShipment = new CellProperty();
                    metricShipment.value = itm.shipmentId ? itm.shipmentId : ' ';
                    propMetrics.push(metricShipment);

                    tmp.METRIC_LIST.unshift(...propMetrics);

                    tmp.METRIC_LIST.forEach((sitm : CellMetric) => {
                        sitm.parentRunId = itm.id;
                    });

                    // Add it
                    flattened.push(tmp);

                });

                response.data = flattened;
                response.status = StatusCode.Success;
            }
        }
        catch (err) {
            response.status = this._handleError(err);
        }

        this._hideLoading();
        return response;
    }

    /**
     * Get the orders for a production run
     * @returns An API response of order objects 
     */
     public getProductionRunOrders = async (runId : number) => {

        this._showLoading();
        const response = new ApiResponse<UnplannedDeliveryOrder[]>();

        try {

            const orders = await Api.GetList(
                UnplannedDeliveryOrder,
                ApiEndpoints.PRODUCTION_RUN_ORDERS.formattify(runId)
            );

            if (orders) {
                response.data = orders;
                response.status = StatusCode.Success;
            }
        }
        catch (err) {
            response.status = this._handleError(err);
        }

        this._hideLoading();
        return response;
    }    

    /**
     * Cancel the provided production run
     * @param runId The ID of the run to start
     */
     public cancelProductionRun = async (runId: number): Promise<ApiResponse<boolean>> => {

        let response = new ApiResponse<boolean>();

        try {

            const result = await this._post<boolean>(
                ApiEndpoints.PRODUCTION_RUN_CANCEL.formattify(runId),
                {}
            );

            response = new ApiResponse<boolean>();
            response.status = result.status;
            response.data = true;
        }
        catch (err) {
            response.status = this._handleError(err);
        }

        return response;
    }

    /**
     * Complete the provided production run
     * @param runId The ID of the run to start
     */
    public completeProductionRun = async (runId: number): Promise<ApiResponse<boolean>> => {

        let response = new ApiResponse<boolean>();

        try {

            const result = await this._post<boolean>(
                ApiEndpoints.PRODUCTION_RUN_COMPLETE.formattify(runId),
                {}
            );

            response = new ApiResponse<boolean>();
            response.status = result.status;
            response.data = true;
        }
        catch (err) {
            response.status = this._handleError(err);
        }

        return response;
    }

    /**
     * Mark the provided job as printed
     * @param runId The ID of the run that the job belongs to
     * @param jobId The ID of the jub to print
     */
    public markJobPrinted = async (updates : ProductionJobUpdate[]): Promise<ApiResponse<ProductionJobUpdateResult[]>> => {

        let response = new ApiResponse<ProductionJobUpdateResult[]>();
        this._showLoading();

        try {

            const reqs : Promise<ApiResponse<boolean>>[] = [];

            // Put all our print requests in a queue
            updates.forEach((itm : ProductionJobUpdate) => {
                const tmpReq = this._postWithError<boolean>(
                    ApiEndpoints.PRODUCTION_RUN_JOB_PRINT.formattify(itm.jobId),
                    {}
                );

                reqs.push(tmpReq);
            });

            // Await all them completing 
            const results = await Promise.allSettled(reqs);

            // Check if we had any errors, and if so, toast a generic message
            // since we can't say for certain which 
            const requestUpdates : ProductionJobUpdateResult[] = [];
            let absoluteSuccess = true;

            results.forEach((res : PromiseSettledResult<ApiResponse<boolean>>, idx : number) => {

                const reqRes = {
                    jobId: updates[idx].jobId,
                    runId: updates[idx].runId,
                    status: StatusCode.Success
                } as ProductionJobUpdateResult;

                if (res.status === "fulfilled" && res.value && res.value.status !== StatusCode.Success) {
                    absoluteSuccess = false;
                    reqRes.status = StatusCode.Failure;
                }
                else if (res.status === "rejected") {
                    absoluteSuccess = false;
                    reqRes.status = StatusCode.Failure;
                }

                requestUpdates.push(reqRes);
            });

            if (!absoluteSuccess) {
                this._showToast("Printing jobs was completed with errors.", 'warning');
            }

            response = new ApiResponse<ProductionJobUpdateResult[]>();
            response.status = StatusCode.Success;
            response.data = requestUpdates;
        }
        catch (err) {
            response.status = this._handleError(err);
        }

        this._hideLoading();
        return response;
    }

    /**
     * Mark the provided job as completed
     * @param runId The ID of the run that the job belongs to
     * @param jobId The ID of the jub to complete
     */
    public markJobComplete = async (updates : ProductionJobUpdate[]): Promise<ApiResponse<ProductionJobUpdateResult[]>> => {

        let response = new ApiResponse<ProductionJobUpdateResult[]>();
        this._showLoading();

        try {

            const reqs : Promise<ApiResponse<boolean>>[] = [];

            // Put all our print requests in a queue
            updates.forEach((itm : ProductionJobUpdate) => {
                const tmpReq = this._postWithError<boolean>(
                    ApiEndpoints.PRODUCTION_RUN_JOB_COMPLETE.formattify(itm.jobId),
                    {}
                );

                reqs.push(tmpReq);
            });

            // Await all them completing 
            const results = await Promise.allSettled(reqs);

            // Check if we had any errors, and if so, toast a generic message
            // since we can't say for certain which 
            let absoluteSuccess = true;
            const requestUpdates : ProductionJobUpdateResult[] = [];

            results.forEach((res : PromiseSettledResult<ApiResponse<boolean>>, idx : number) => {

                const reqRes = {
                    jobId: updates[idx].jobId,
                    runId: updates[idx].runId,
                    status: StatusCode.Success
                } as ProductionJobUpdateResult;

                if (res.status === "fulfilled" && res.value && res.value.status !== StatusCode.Success) {
                    absoluteSuccess = false;
                }
                else if (res.status === "rejected") {
                    absoluteSuccess = false;
                }

                requestUpdates.push(reqRes);
            });

            if (!absoluteSuccess) {
                this._showToast("Completing jobs was completed with errors.", 'warning');
            }

            response = new ApiResponse<ProductionJobUpdateResult[]>();
            response.status = StatusCode.Success;
            response.data = requestUpdates;
        }
        catch (err) {
            response.status = this._handleError(err);
        }

        this._hideLoading();
        return response;
    }

    /**
     * Get all the unplanned pickup production tasks
     * @param jobId The ID of the jub to complete
     * @returns An API response of UnplannedPickupTaskGroup objects 
     */
     public getRankings = async (quantity : number, beforeRank : string|undefined, afterRank : string|undefined) => {

        this._showLoading();
        const response = new ApiResponse<RankingsResponse>();

        try {

            let url = ApiEndpoints.PRODUCTION_RUN_GET_RANKINGS.formattify(quantity);

            if (afterRank && afterRank.length > 0) {
                url += `&after=${afterRank}`;
            }

            if (beforeRank && beforeRank.length > 0) {
                url += `&before=${beforeRank}`;
            }
            
            const rankings = await Api.Get(RankingsResponse, url);

            if (rankings) {
                response.data = rankings;
                response.status = StatusCode.Success;
            }
        }
        catch (err) {
            response.status = this._handleError(err);
        }

        this._hideLoading();
        return response;
    }
    
    /**
     * Get all the unplanned pickup production tasks
     * @param jobId The ID of the jub to complete
     * @returns An API response of UnplannedPickupTaskGroup objects 
     */
     public updateRunRank = async (runId : number, rank : string) => {

        this._showLoading();
        const response = new ApiResponse<boolean>();

        try {

            const vm = new UpdateRunRanking();
            vm.productionRunId = runId;
            vm.rank = rank;
            
            const result = await Api.Put(
                ApiEndpoints.PRODUCTION_RUN_UPDATE_RANK.formattify(runId),
                vm
            );

            if (result) {
                response.data = true;
                response.status = StatusCode.Success;
            }
        }
        catch (err) {
            response.status = this._handleError(err);
        }

        this._hideLoading();
        return response;
    }

}
